import React, {
  forwardRef,
  ForwardRefExoticComponent,
  JSX,
  ReactElement,
  Ref,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@emotion/react';
import ReactDatePicker from 'react-datepicker';
import { DataTestIdProp } from '../../../../Models/DataTestIdProp';
import useConfig from '../../../Hooks/useConfig';
import { appService } from '../../../Hooks/Contexts';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ReactDatePickerRenderer from './ReactDatePickerRenderer';
import renderCustomHeader from './RenderCustomHeader';
import { OnChangeDate, OnChangeDateIntersection } from './DateWithKeyboardSupport';
import DatePickerInModal, { CustomsProps, DatePickerProps } from './DatePickerInModal';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export interface DatePickerRef {
  openDatePicker: () => void;
  closeDatePicker: () => void;
  forceUpdate: () => void;
}

const DatePicker: ForwardRefExoticComponent<
  DatePickerProps & DataTestIdProp & CustomsProps & { ref?: Ref<DatePickerRef> }
> = forwardRef(
  (
    props: DatePickerProps & DataTestIdProp & CustomsProps,
    ref: Ref<DatePickerRef>
  ): JSX.Element => {
    const theme = useTheme();
    const config = useConfig();

    const {
      placeholderText,
      date,
      customInput,
      onChange,
      dataTestId,
      withHeader,
      locale = appService.lang || config.defaultLanguage,
      noDatePickerOverflow,
      onCalendarOpen,
      onCalendarClose,
      minDate,
      maxDate,
      customCalendarContainer,
      focusSelectedMonth,
      withKeyboardSupport,
      setValueToNullWhenFormatIsIncorrect,
      onInvalidInputFormat,
      updatePackageAvailability,
      showInModal,
      inline,
      onMonthChange,
      renderDayContents,
      noInitialValueInput
    } = props;

    const reactDatePickerRef = useRef<ReactDatePicker>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      openDatePicker() {
        reactDatePickerRef?.current?.setOpen(true);
      },
      closeDatePicker() {
        reactDatePickerRef?.current?.setOpen(false);
      },
      forceUpdate() {
        reactDatePickerRef?.current?.forceUpdate();
      }
    }));

    const toggleDropdown = useCallback((): void => {
      setIsModalOpen(value => !value);
    }, []);

    const onInputChange = useCallback(
      (newDate: OnChangeDate | null): void => {
        if (setValueToNullWhenFormatIsIncorrect && onInvalidInputFormat && !newDate) {
          onInvalidInputFormat();
        }
        if (!!onChange && newDate) {
          onChange(newDate as OnChangeDateIntersection);
        }
      },
      [setValueToNullWhenFormatIsIncorrect, onChange, onInvalidInputFormat]
    );

    const handleMonthChange = useCallback(
      (onMonthChangeDate: Date): void => {
        if (!!updatePackageAvailability) {
          updatePackageAvailability(onMonthChangeDate);
        }
        if (!!onMonthChange) {
          onMonthChange(onMonthChangeDate);
        }
      },
      [updatePackageAvailability, onMonthChange]
    );

    const renderDatePicker = (
      alwaysRenderInline: boolean,
      renderInline?: boolean
    ): ReactElement => {
      return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <ReactDatePickerRenderer
          {...props}
          theme={theme}
          reactDatePickerRef={reactDatePickerRef}
          dataTestId={dataTestId}
          locale={locale}
          calendarContainer={customCalendarContainer}
          withHeader={withHeader}
          renderCustomHeader={renderCustomHeader}
          renderDayContents={renderDayContents}
          showInModal={showInModal}
          toggleDropdown={toggleDropdown}
          onChange={onChange}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
          customInput={customInput}
          withKeyboardSupport={withKeyboardSupport}
          placeholderText={placeholderText}
          selected={date}
          focusSelectedMonth={focusSelectedMonth}
          minDate={minDate}
          maxDate={maxDate}
          onMonthChange={handleMonthChange}
          onInputChange={onInputChange}
          inline={alwaysRenderInline ? true : renderInline}
          noDatePickerOverflow={noDatePickerOverflow}
          noInitialValueInput={noInitialValueInput}
        />
      );
    };

    return showInModal ? (
      <DatePickerInModal
        {...props}
        onInputChange={onInputChange}
        toggleDropdown={toggleDropdown}
        isModalOpen={isModalOpen}
      >
        {renderDatePicker(true)}
      </DatePickerInModal>
    ) : (
      renderDatePicker(false, inline)
    );
  }
);

export default DatePicker;
